"use client";

import { type ReactNode, useState } from "react";
import { useFormStatus } from "react-dom";
import { money } from "@/lib/money";
import { takeAllAction } from "@/app/actions/listing";
import { ProfitCalculator } from "@/components/feed/profit-calculator";

// Submit button that reflects the in-flight request the instant it is clicked:
// while the server action runs, swap the label for a spinner and disable the
// button so the click registers immediately instead of feeling unresponsive.
function TakeSubmitButton({
  className,
  children,
}: {
  className: string;
  children: ReactNode;
}) {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      className={`${className} flex items-center justify-center gap-2 ${
        pending ? "opacity-70 pointer-events-none" : ""
      }`}
    >
      {pending ? (
        <svg
          className="h-4 w-4 animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
          />
        </svg>
      ) : (
        children
      )}
    </button>
  );
}

// Client shell for the feed card's calculator + action buttons. Owns the qty
// state so the "Take <qty>" button label and its submitted quantity stay in
// sync with the Profit Calculator's stepper.
export function FeedCardCta({
  listingId,
  costCents,
  msrpCents,
  minOrder,
  unitsAvailable,
  spendingPower,
}: {
  listingId: string;
  costCents: number;
  msrpCents: number;
  minOrder: number;
  unitsAvailable: number;
  spendingPower: number; // cents
}) {
  const step = minOrder || 1;
  // Default to the MOQ, but never above what is actually available.
  const [qty, setQty] = useState(Math.min(step, unitsAvailable || step));

  return (
    <>
      <div className="mb-4">
        <ProfitCalculator
          costCents={costCents}
          msrpCents={msrpCents}
          minOrder={minOrder}
          unitsAvailable={unitsAvailable}
          spendingPower={spendingPower}
          qty={qty}
          onQtyChange={setQty}
        />
      </div>

      <div className="flex gap-2">
        <form action={takeAllAction} className="flex-1">
          <input type="hidden" name="listingId" value={listingId} />
          <input type="hidden" name="quantity" value={qty} />
          <TakeSubmitButton className="w-full py-3 border border-white/15 text-white text-sm font-medium rounded-xl hover:bg-white/5 transition-colors text-center">
            Take {qty.toLocaleString()} &bull; {money(qty * costCents, 0)}
          </TakeSubmitButton>
        </form>
        <form action={takeAllAction} className="flex-1">
          <input type="hidden" name="listingId" value={listingId} />
          <input type="hidden" name="quantity" value={unitsAvailable} />
          <TakeSubmitButton className="w-full py-3 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-500 transition-colors">
            Take All &bull; {money(unitsAvailable * costCents, 0)}
          </TakeSubmitButton>
        </form>
      </div>
    </>
  );
}
